import React, { useMemo, useState } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  startOfWeek,
  addDays,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/shadcnComponents/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcnComponents/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcnComponents/ui/popover";

interface Post {
  postUrn: number;
  postDescription: string;
  postCreatedOn: Date;
}

interface CalendarProps {
  posts: Post[];
}

const PostCalendar: React.FC<CalendarProps> = ({ posts }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const today = new Date();

  const firstPostDate = useMemo(() => {
    if (posts.length === 0) return new Date();
    return posts.reduce((earliest, post) => {
      const postDate = new Date(post?.postCreatedOn);
      return postDate < earliest ? postDate : earliest;
    }, new Date(posts[0]?.postCreatedOn));
  }, [posts]);

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentDate),
    end: endOfMonth(currentDate),
  });

  const startDayOfMonth = startOfMonth(currentDate);
  const startDayOfWeek = startOfWeek(startDayOfMonth);
  const endDayOfMonth = endOfMonth(currentDate);

  const getPostsForDate = (date: Date) => {
    return posts.filter((post) =>
      isSameDay(new Date(post?.postCreatedOn), date)
    );
  };

  const renderCell = (day: Date) => {
    const postsOnDay = getPostsForDate(day);
    const dotCount = Math.min(postsOnDay.length, 3);
    const isFutureDate = day > new Date();

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            disabled={isFutureDate} // Disable button for future dates
            className={`h-10 w-10 p-0 font-normal hover:bg-[#FF1F1F33] hover:text-white ${
              !isSameMonth(day, currentDate)
                ? "text-gray-500 dark:text-gray-400"
                : isSameDay(day, new Date()) && isSameDay(day, currentDate)
                ? "bg-[#4B72FF] text-white"
                : isSameDay(day, new Date())
                ? "text-[#4B72FF] font-bold"
                : isSameDay(day, currentDate)
                ? "bg-[#FF1F1F33] text-white"
                : ""
            }`}
            onClick={() => setCurrentDate(day)}
          >
            <div className="flex flex-col items-center">
              <time dateTime={format(day, "yyyy-MM-dd")}>
                {format(day, "d")}
              </time>
              {dotCount > 0 && (
                <div className="flex justify-center mt-1">
                  {[...Array(dotCount)]?.map((_, i) => (
                    <div
                      key={i}
                      className="w-1 h-1 rounded-full bg-red-500 mx-0.5"
                    />
                  ))}
                </div>
              )}
            </div>
          </Button>
        </PopoverTrigger>
        {postsOnDay.length > 0 && (
          <PopoverContent className="max-w-72 bg-[#292F35] border-[#292F35]">
            <div className="grid gap-4">
              <div className="space-y-2">
                <h4 className="font-medium leading-none text-gray-200 text-sm">
                  Posts on {format(day, "MMMM d, yyyy")}
                </h4>
                <ul className="list-decimal text-gray-300">
                  {postsOnDay.map((post) => (
                    <li key={post?.postUrn} className="line-clamp-1 text-xs">
                      {`[${format(
                        new Date(post?.postCreatedOn),
                        "hh:mm a"
                      )}] ${post?.postDescription?.slice(0, 50)}`}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </PopoverContent>
        )}
      </Popover>
    );
  };

  const changeMonth = (increment: number) => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() + increment);
      return newDate;
    });
  };

  const changeYear = (year: string) => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setFullYear(parseInt(year));
      return newDate;
    });
  };

  return (
    <div className="w-full max-w-md mx-auto text-white rounded-lg">
      <div className="flex items-center justify-between mb-1 space-x-2">
        <Button
          size="icon"
          onClick={() => changeMonth(-1)}
          className="text-white border-gray-700 hover:bg-gray-800 bg-transparent"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center space-x-2">
          <Select
            value={format(currentDate, "M")}
            onValueChange={(value) =>
              setCurrentDate(
                new Date(currentDate.getFullYear(), parseInt(value) - 1)
              )
            }
          >
            <SelectTrigger className="w-[120px] bg-gray-800 border-gray-700 text-white">
              <SelectValue placeholder="Select month">
                {format(currentDate, "MMMM")}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white">
              {Array.from(
                {
                  length:
                    currentDate.getFullYear() === today.getFullYear()
                      ? today.getMonth() + 1
                      : 12,
                },
                (_, i) => {
                  const monthDate = new Date(currentDate.getFullYear(), i, 1);
                  return (
                    (currentDate.getFullYear() > firstPostDate.getFullYear() ||
                      monthDate.getMonth() >= firstPostDate.getMonth()) && (
                      <SelectItem
                        key={i}
                        value={(i + 1).toString()}
                        className="text-white hover:bg-gray-700"
                      >
                        {format(monthDate, "MMMM")}
                      </SelectItem>
                    )
                  );
                }
              )}
            </SelectContent>
          </Select>
          <Select
            value={currentDate.getFullYear().toString()}
            onValueChange={(value) => changeYear(value)}
          >
            <SelectTrigger className="w-[100px] bg-gray-800 border-gray-700 text-white">
              <SelectValue placeholder="Select year">
                {currentDate.getFullYear()}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-white">
              {Array.from(
                {
                  length: today.getFullYear() - firstPostDate.getFullYear() + 1,
                },
                (_, i) => {
                  const year = firstPostDate.getFullYear() + i;
                  return (
                    <SelectItem
                      key={i}
                      value={year.toString()}
                      className="text-white hover:bg-gray-700"
                    >
                      {year}
                    </SelectItem>
                  );
                }
              )}
            </SelectContent>
          </Select>
        </div>
        <Button
          size="icon"
          onClick={() => changeMonth(1)}
          className="text-white border-gray-700 hover:bg-gray-800 bg-transparent"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid grid-cols-7">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div
            key={day}
            className="text-center font-medium text-sm py-2 text-gray-400"
          >
            {day}
          </div>
        ))}
        {Array.from({ length: startDayOfMonth.getDay() }).map((_, index) => (
          <div key={index} className="text-center">
            {renderCell(addDays(startDayOfWeek, index))}
          </div>
        ))}
        {daysInMonth.map((day) => (
          <div key={day.toString()} className="text-center">
            {renderCell(day)}
          </div>
        ))}
        {Array.from({ length: 6 - endDayOfMonth.getDay() }).map((_, index) => (
          <div key={index} className="text-center">
            {renderCell(addDays(endDayOfMonth, index + 1))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PostCalendar;
